import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RecipeIngredientRow from "./RecipeIngredientRow";
import YieldModal from "../../../components/Modals/YieldModal";
import IngredientQuantityModal from "../../../components/Modals/IngredientQuantityModal";
import utilFunctions from "../../../utilities/utilFunctions";
import { cloneDeep } from "lodash";
import Store from "../../../Store/Store";
import { useMutation, useQueryClient } from "react-query";
import inventoryClient from "../../../clients/inventoryClient";
import YieldToggle from "./YieldToggle";
import useVendorCurrency from "../../../hooks/useVendorCurrency";
import EditRecipeModal from "../../../components/Modals/EditRecipeModal";
import { recipeIngredientTypes } from "../../../clients/inventoryObjects";
import DescriptionToolTip from "../../../components/DescriptionToolTip";
import useEstablishmentSettings from "../../../hooks/useEstablishmentSettings";
import ImageControl from "../../../components/ImageControl";
import CreateProductModal from "../../../components/Modals/CreateProductModal";
import CopyRecipeModal from "../../../components/Modals/CopyRecipeModal";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate, useParams } from "react-router-dom";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RecipePreviewModal from "./RecipePreviewModal";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
} from "@mui/material";

const RecipeForm = ({
  recipe,
  establishmentUoMs,
  productLibrary,
  productLibraryMap,
  establishmentRecipes,
  familyData,
  vendorData,
  expenseGroupData,
  isSpecEstablishment,
  specPartnersData,
}) => {
  const store = useContext(Store);
  const { establishmentid } = useParams();
  const [yieldModalOpen, toggleYieldModalOpen] = useState(false);
  const [quantityModalOpen, toggleQuantityModalOpen] = useState(false);
  const [editRecipeModalOpen, toggleEditRecipeModalOpen] = useState(false);
  const [createProductModalOpen, toggleCreateProductModalOpen] =
    useState(false);
  const [recipeCopy, setRecipeCopy] = useState({});
  const [laborCostDisplay, setLaborCostDisplay] = useState(0);
  const [laborDurationDisplay, setLaborDurationDisplay] = useState(null);
  const [laborDurationInvalid, toggleLaborDurationInvalid] = useState(null);
  const [isImageControlOpen, toggleIsImageControlOpen] = useState(false);
  const [createProductRecipeRef, setCreateProductRecipeRef] = useState(null);
  const [createProductPrefill, setCreateProductPrefill] = useState("");
  const [alertSuccess, setAlertSuccess] = useState(undefined);
  const [isSpecPartnerRecipe, setIsSpecPartnerRecipe] = useState(false);
  const [selectedSpecPartner, setSelectedSpecPartner] = useState("");
  const [costingSummaryOpen, setCostingSummaryOpen] = useState(true);
  const [costingBreakdownOpen, setCostingBreakdownOpen] = useState(true);
  const [copyRecipeModalOpen, setCopyRecipeModalOpen] = useState(false);
  const navigate = useNavigate();
  const [menuPriceDisplay, setMenuPriceDisplay] = useState(0);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const { data: settings } = useEstablishmentSettings(establishmentid);
  const { locale, currency, currencySymbol } = useVendorCurrency(
    settings?.inventorySettings?.currency,
  );
  const [ingredientForQuantityModal, setIngredientForQuantityModal] =
    useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setRecipeCopy(cloneDeep(recipe));
    setIsSpecPartnerRecipe(recipe?.isSpecPartnerRecipe || false);
    setSelectedSpecPartner(recipe?.specPartnerId || "");
    setLaborCostDisplay(
      utilFunctions.convertCentsToLargerFormatCurrency(
        recipe?.laborCost?.wage?.amount,
        locale,
        currency,
      ),
    );

    if (
      recipe?.laborCost?.hourDuration === 0 &&
      recipe?.laborCost?.minuteDuration === 0
    ) {
      setLaborDurationDisplay(null);
    } else {
      convertLaborToDisplay(
        recipe?.laborCost?.hourDuration,
        recipe?.laborCost?.minuteDuration,
      );
    }

    setMenuPriceDisplay(
      utilFunctions.convertCentsToLargerFormatCurrency(
        recipe?.approximateMenuPrice?.amount || 0,
        locale,
        currency,
      ),
    );
  }, [recipe]);

  const updateLocalRecipeCopy = (property, newValue) => {
    const updatedRecipe = cloneDeep(recipeCopy);
    updatedRecipe[property] = newValue;
    setRecipeCopy(updatedRecipe);
  };

  const handleOpenCreateProductModal = (ingredientId, textInputValue) => {
    document.activeElement?.blur();
    setCreateProductRecipeRef(ingredientId);
    setCreateProductPrefill(textInputValue);
    toggleCreateProductModalOpen(true);
  };

  const handleCloseCreateProductModal = () => {
    // TODO: DONT LET IT CLOSE IF IN PROCESS OF CREATING PRODUCT?
    toggleCreateProductModalOpen(false);
    setCreateProductPrefill("");
  };

  const recipeMutation = useMutation(inventoryClient.patchRecipe, {
    onSuccess: (result, variables) => {
      // because we don't know the new image url until the server tells us, we can't
      // optimistically update that before the call, so we have a special check
      // to set the image url once its handed back
      if (variables.imageUrlUpdate !== undefined) {
        const updatedRecipe = cloneDeep(recipeCopy);
        updatedRecipe.imageUrl = result.recipe.imageUrl;
        setRecipeCopy(updatedRecipe);
      }

      if (variables.laborCostUpdate !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [
            "recipes",
            {
              establishmentId: store.currentEstablishment?.id,
              recipeId: recipe.id,
            },
          ],
        });
        queryClient.refetchQueries({
          queryKey: [
            "recipes",
            {
              establishmentId: store.currentEstablishment?.id,
              recipeId: recipe.id,
            },
          ],
        });
      }

      queryClient.invalidateQueries(["menu"], {
        refetchActive: true,
        //refetchInactive: true,
      });
      // queryClient.refetchQueries({
      //   queryKey: [
      //     "menu",
      //   ]
      // });
    },
  });

  const copyRecipeMutation = useMutation(inventoryClient.copyRecipe, {
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["recipes-paginated", { establishmentId: establishmentid }],
      });
      navigate(`/spec/${establishmentid}/recipes/${result?.recipe?.id}`);
    },
  });

  const handleCopyRecipe = async (recipeId) => {
    await copyRecipeMutation.mutateAsync({
      establishmentId: establishmentid,
      recipeId: recipeId,
    });
  };

  const createProductMutation = useMutation(
    inventoryClient.addProductsReactQuery,
  );

  const handleCreateProduct = async (product) => {
    try {
      const productCreationResult = await createProductMutation.mutateAsync({
        establishmentId: establishmentid,
        products: [product],
      });
      queryClient.invalidateQueries({
        queryKey: ["inventory-paginated"],
      });
      queryClient.refetchQueries({
        queryKey: ["inventory-paginated"],
        type: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["inventory"],
      });
      queryClient.refetchQueries({
        queryKey: ["inventory"],
        type: "all",
      });

      const newProductId = productCreationResult.data.products[0].id;
      const updatePayload = {
        establishmentId: store.currentEstablishment?.id,
        recipeId: recipe.id,
        ingredientId: createProductRecipeRef,
        productUpdate: {
          value: newProductId,
        },
      };
      await ingredientMutation.mutateAsync(updatePayload);
      queryClient.invalidateQueries({
        queryKey: [
          "recipes",
          {
            establishmentId: store.currentEstablishment?.id,
            recipeId: recipe.id,
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "menu",
          {
            establishmentId: store.currentEstablishment?.id,
            recipeId: recipe.id,
          },
        ],
      });
      queryClient.refetchQueries({
        queryKey: [
          "recipes",
          {
            establishmentId: store.currentEstablishment?.id,
            recipeId: recipe.id,
          },
        ],
      });
      setAlertSuccess(
        "Product added and linked to your ingredient successfully!",
      );
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const ingredientMutation = useMutation(inventoryClient.patchIngredient, {
    onSuccess: (result) => {
      const updatedRecipe = cloneDeep(recipeCopy);
      updatedRecipe.ingredients = updatedRecipe.ingredients.map((x) => {
        if (x.id === result.ingredient.id) {
          return result.ingredient;
        } else {
          return x;
        }
      });
      setRecipeCopy(updatedRecipe);
    },
  });

  const toggleSpecPartnerMutation = useMutation(
    inventoryClient.toggleSpecPartnerRecipe,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "recipes",
            {
              establishmentId: store.currentEstablishment?.id,
              recipeId: recipe.id,
            },
          ],
        });
      },
    },
  );

  const assignSpecPartnerMutation = useMutation(
    inventoryClient.assignSpecPartnerToRecipe,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "recipes",
            {
              establishmentId: store.currentEstablishment?.id,
              recipeId: recipe.id,
            },
          ],
        });
      },
    },
  );

  const addIngredientMutation = useMutation(inventoryClient.addIngredient, {
    onSuccess: () => {
      // const updatedRecipe = cloneDeep(recipeCopy);
      // updatedRecipe.ingredients.push(result.ingredient);
      // setRecipeCopy(updatedRecipe);
      queryClient.invalidateQueries({
        queryKey: [
          "recipes",
          {
            establishmentId: store.currentEstablishment?.id,
            recipeId: recipe.id,
          },
        ],
      });
      queryClient.refetchQueries({
        queryKey: [
          "recipes",
          {
            establishmentId: store.currentEstablishment?.id,
            recipeId: recipe.id,
          },
        ],
      });
    },
  });

  const handleUpdateRecipe = async (property, newValue) => {
    const updatedRecipe = cloneDeep(recipeCopy);
    let payload = {
      establishmentId: store.currentEstablishment?.id,
      recipeId: recipe.id,
    };

    // we use the value already set on the copy if no value is provided.
    // this is because we have certain fields that are updated dynamically
    // as the user types locally, and then we want to update the server in one shot
    updatedRecipe[property] = newValue || recipeCopy[property];
    payload[`${property}Update`] = { value: newValue || recipeCopy[property] };
    await recipeMutation.mutateAsync(payload);

    // image url gets custom created by server, so we don't know the url. We rely on OnSuccess
    // function of mutation to update this value
    if (property !== "imageUrl") {
      setRecipeCopy(updatedRecipe);
    }
  };

  const handleUpdateIngredient = async (property, newValue, ingredientId) => {
    let payload = {
      establishmentId: store.currentEstablishment?.id,
      recipeId: recipe.id,
      ingredientId: ingredientId,
    };

    payload[`${property}Update`] = { value: newValue };
    await ingredientMutation.mutateAsync(payload);
  };

  const handleAddIngredient = async () => {
    addIngredientMutation.mutate({
      recipeId: recipeCopy.id,
      establishmentId: store.currentEstablishment?.id,
    });
  };

  const handleToggleSpecPartner = async () => {
    const newValue = !isSpecPartnerRecipe;
    setIsSpecPartnerRecipe(newValue);
    await toggleSpecPartnerMutation.mutateAsync({ recipeId: recipe.id });
  };

  const handleSpecPartnerChange = async (event) => {
    const partnerId = event.target.value;
    setSelectedSpecPartner(partnerId);
    await assignSpecPartnerMutation.mutateAsync({
      recipeId: recipe.id,
      partnerId: partnerId,
    });
  };

  const handleToggleQuantityModal = (shouldOpenModal, ingredient) => {
    setIngredientForQuantityModal(ingredient);
    toggleQuantityModalOpen(shouldOpenModal);
  };

  const handleIngredientUpdateCallback = (ingredient) => {
    const updatedRecipe = cloneDeep(recipeCopy);
    updatedRecipe.ingredients = updatedRecipe.ingredients.map((x) => {
      if (x.id === ingredient.id) {
        return ingredient;
      } else {
        return x;
      }
    });
    setRecipeCopy(updatedRecipe);
  };

  const handleDeleteIngredientCallback = () => {
    queryClient.invalidateQueries({
      queryKey: [
        "recipes",
        {
          establishmentId: store.currentEstablishment?.id,
          recipeId: recipe.id,
        },
      ],
    });
    queryClient.refetchQueries({
      queryKey: [
        "recipes",
        {
          establishmentId: store.currentEstablishment?.id,
          recipeId: recipe.id,
        },
      ],
    });
  };

  const handleLaborCostUpdate = async () => {
    const centsValue = utilFunctions.convertCurrencyToCents(laborCostDisplay);
    const newLaborCost = cloneDeep(recipeCopy.laborCost);
    newLaborCost.wage.amount = centsValue;
    await handleUpdateRecipe("laborCost", newLaborCost);
    setLaborCostDisplay(
      utilFunctions.convertCentsToLargerFormatCurrency(
        centsValue,
        locale,
        currency,
      ),
    );
  };

  const handleMenuPriceUpdate = async () => {
    const centsValue = utilFunctions.convertCurrencyToCents(menuPriceDisplay);

    // Make the API call
    const payload = {
      establishmentId: store.currentEstablishment?.id,
      recipeId: recipe.id,
      approximateMenuPriceUpdate: { value: centsValue },
    };
    await recipeMutation.mutateAsync(payload);

    // Update local state with proper object structure
    const updatedRecipe = cloneDeep(recipeCopy);
    if (updatedRecipe.approximateMenuPrice) {
      updatedRecipe.approximateMenuPrice.amount = centsValue;
    } else {
      updatedRecipe.approximateMenuPrice = { amount: centsValue };
    }
    setRecipeCopy(updatedRecipe);

    setMenuPriceDisplay(
      utilFunctions.convertCentsToLargerFormatCurrency(
        centsValue,
        locale,
        currency,
      ),
    );
  };

  const handleUpdateLaborDuration = async () => {
    toggleLaborDurationInvalid(false);

    const isValidFormat = /^(?:(?:([01]?\d|2[0-3]):)?([0-5]?\d))$/.test(
      laborDurationDisplay,
    );

    if (!isValidFormat) {
      toggleLaborDurationInvalid(true);
      return;
    }

    const updatedLabor = cloneDeep(recipeCopy.laborCost);

    if (laborDurationDisplay.includes(":")) {
      const splitValue = laborDurationDisplay.split(":");
      updatedLabor.hourDuration = splitValue[0];
      updatedLabor.minuteDuration = splitValue[1];
    } else {
      updatedLabor.hourDuration = 0;
      updatedLabor.minuteDuration = laborDurationDisplay;
    }

    await handleUpdateRecipe("laborCost", updatedLabor);
    setLaborDurationDisplay(
      convertLaborToDisplay(
        Number(updatedLabor.hourDuration),
        Number(updatedLabor.minuteDuration),
      ),
    );
  };

  const convertLaborToDisplay = (hour, minute) => {
    let hourStamp = "0";
    let minuteStamp = "0";

    if (hour < 10) {
      hourStamp = hourStamp + hour.toString();
    } else {
      hourStamp = hour.toString();
    }

    if (minute < 10) {
      minuteStamp = minuteStamp + minute.toString();
    } else {
      minuteStamp = minute.toString();
    }

    setLaborDurationDisplay(hourStamp + ":" + minuteStamp);
  };

  const imageControlCallback = async (photoLocation) => {
    await handleUpdateRecipe("imageUrl", photoLocation);
    toggleIsImageControlOpen(false);
  };

  const calculateTotalCost = () => {
    let total = 0;

    recipeCopy?.ingredients?.forEach((x) => {
      if (x.ingredientCost?.isValid && x.includeInCosting) {
        total += x.ingredientCost?.result?.amount || 0;
        total += x.laborCost?.result?.amount || 0;
      }
    });

    total += recipeCopy?.laborCost?.cost?.amount;

    const formatted = utilFunctions.convertCentsToLargerFormatCurrency(
      total,
      locale,
      currency,
    );
    return currencySymbol + formatted.toString();
  };

  const tableCellStyle = {
    padding: 0,
    border: "1px solid",
    borderBottom: "none",
    borderColor: (theme) => theme.palette.terrain[400],
    backgroundColor: (theme) => theme.palette.pure.white,
  };

  const tableHeadStyle = {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 2,
    background: (theme) => theme.palette.terrain[300],
    height: "24px",
    border: "1px solid",
    borderBottom: "none",
    borderColor: (theme) => theme.palette.terrain[400],
    borderTop: "none",
    lineHeight: 1.2,
    fontSize: 10,
    fontWeight: 600,
  };

  const calculateIngredientCostTotal = () => {
    let total = 0;

    recipeCopy?.ingredients?.forEach((x) => {
      if (x.ingredientCost?.isValid && x.includeInCosting) {
        total += x.ingredientCost?.result?.amount || 0;
      }
    });

    const formatted = utilFunctions.convertCentsToLargerFormatCurrency(
      total,
      locale,
      currency,
    );
    return currencySymbol + formatted.toString();
  };

  const calculateLaborCostTotal = () => {
    let total = 0;

    recipeCopy?.ingredients?.forEach((x) => {
      if (
        x.type === recipeIngredientTypes.Recipe &&
        x.laborCost?.result?.amount !== -1
      ) {
        total += x.laborCost?.result?.amount || 0;
      }
    });

    const formatted = utilFunctions.convertCentsToLargerFormatCurrency(
      total,
      locale,
      currency,
    );
    return currencySymbol + formatted.toString();
  };

  // Raw cost helper functions (returns amounts in cents)
  const getRawLaborCost = () => {
    return recipeCopy?.laborCost?.cost?.amount || 0;
  };

  const getRawIngredientCost = () => {
    let total = 0;
    recipeCopy?.ingredients?.forEach((x) => {
      if (x.ingredientCost?.isValid && x.includeInCosting) {
        total += x.ingredientCost?.result?.amount || 0;
      }
    });
    return total;
  };

  const getRawTotalCost = () => {
    let total = 0;

    recipeCopy?.ingredients?.forEach((x) => {
      if (x.ingredientCost?.isValid && x.includeInCosting) {
        total += x.ingredientCost?.result?.amount || 0;
        total += x.laborCost?.result?.amount || 0; // sub-recipe labor costs
      }
    });

    total += recipeCopy?.laborCost?.cost?.amount || 0;

    return total;
  };

  const getRawMenuPrice = () => {
    return recipeCopy?.approximateMenuPrice?.amount || 0;
  };

  const getRawEstimatedProfit = () => {
    return getRawMenuPrice() - getRawTotalCost();
  };

  const calculatePercentageOfMenuPrice = (amountInCents) => {
    const menuPrice = getRawMenuPrice();
    if (menuPrice === 0) return "0";
    const percentage = (amountInCents / menuPrice) * 100;
    return percentage.toFixed(2);
  };

  const getIngredientCostChartData = () => {
    const chartData = [];

    recipeCopy?.ingredients?.forEach((ingredient, index) => {
      if (ingredient.ingredientCost?.isValid && ingredient.includeInCosting) {
        const cost = ingredient.ingredientCost?.result?.amount || 0;
        if (cost > 0) {
          // Get ingredient name from product or recipe
          let ingredientName = "Unknown";

          if (ingredient.type === recipeIngredientTypes.Recipe) {
            ingredientName =
              ingredient.recipeIngredientReference?.name || "Recipe";
          } else if (ingredient.productId) {
            const product = productLibraryMap?.[ingredient.productId];
            ingredientName = product?.name || "Product";
          }

          // Truncate ingredient name if it's too long
          const capitalizedName =
            utilFunctions.capitalizeString(ingredientName);
          const truncatedName =
            capitalizedName.length > 20
              ? capitalizedName.substring(0, 20) + "..."
              : capitalizedName;

          chartData.push({
            id: index,
            value: cost / 100, // Convert cents to currency
            label: `${truncatedName} - ${currencySymbol}${utilFunctions.convertCentsToLargerFormatCurrency(
              cost,
              locale,
              currency,
            )}`,
          });
        }
      }
    });

    return chartData;
  };

  const getChartHeight = () => {
    const chartData = getIngredientCostChartData();
    const itemCount = chartData.length;
    // Base height is 190px, but if we have more than 7 items, increase height
    // Each item needs approximately 20px of vertical space in the legend
    if (itemCount <= 7) {
      return 158;
    }
    return Math.max(190, 150 + itemCount * 20);
  };

  const filteredRecipeOptions = establishmentRecipes?.filter(
    (x) => x.id !== recipeCopy.id,
  );

  return (
    <Box>
      <Box sx={{ marginTop: 4, marginBottom: 2 }}>
        <Grid
          container
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Grid item>
            <Typography variant="h2">
              {utilFunctions.capitalizeString(recipeCopy.name || "")}
            </Typography>
          </Grid>
          <Grid item>
            <Grid
              container
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => toggleEditRecipeModalOpen(true)}
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                >
                  <Typography variant="largeButton">Edit Details</Typography>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => setCopyRecipeModalOpen(true)}
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                >
                  <Typography variant="largeButton">
                    Duplicate Recipe
                  </Typography>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => setPreviewModalOpen(true)}
                  startIcon={<PrintOutlinedIcon sx={{ fontSize: 14 }} />}
                >
                  <Typography variant="largeButton">Print Recipe</Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "140px",
            height: "32px",
            borderRadius: 1,
            border: "1px solid",
            borderColor: (theme) => theme.palette.terrain[300],
            backgroundColor: (theme) => theme.palette.pure.white,
          }}
        >
          <Typography variant="body1">Recipe Status: </Typography>
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.info.main,
              marginLeft: 1,
            }}
          >
            {recipeCopy.isActive ? "Active" : "Inactive"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "140px",
            height: "32px",
            borderRadius: 1,
            border: "1px solid",
            borderColor: (theme) => theme.palette.terrain[300],
            backgroundColor: (theme) => theme.palette.pure.white,
            marginLeft: 2,
          }}
        >
          <Typography variant="body1">Show to Staff: </Typography>
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.info.main,
              marginLeft: 1,
            }}
          >
            {recipeCopy.isSharedWithStaff ? "Visible" : "Hidden"}
          </Typography>
        </Box>
      </Box>

      {/* Costing Summary & Cost Breakdown - Horizontal Layout */}

      <Box sx={{ marginTop: 6 }}>
        <Grid container spacing={3}>
          {/* Image Control */}
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                width: "100%",
                minWidth: "260px",
                borderRadius: 4,
                backgroundColor: (theme) => theme.palette.pure.white,
                border: "2px solid",
                borderColor: (theme) => theme.palette.terrain[300],
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "260px",
              }}
            >
              <ImageControl
                size="extraLarge"
                imageSrc={recipeCopy.imageUrl}
                imageControlCallback={imageControlCallback}
                isImageControlOpen={isImageControlOpen}
                toggleIsImageControlOpen={toggleIsImageControlOpen}
                isLoading={recipeMutation?.isLoading}
              />
            </Box>
          </Grid>

          {/* Recipe Details - Yield, Duration, Wage */}
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                width: "100%",
                height: "260px",
                borderRadius: 4,
                backgroundColor: (theme) => theme.palette.pure.white,
                border: "2px solid",
                borderColor: (theme) => theme.palette.terrain[300],
                padding: 4,
              }}
            >
              <Grid container spacing={3}>
                {/* Yield */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "60px",
                      backgroundColor: (theme) => theme.palette.pure.white,
                      padding: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        marginBottom: 1,
                      }}
                    >
                      Yield
                    </Typography>
                    <YieldToggle
                      recipe={recipeCopy}
                      toggleYieldModalOpen={toggleYieldModalOpen}
                    />
                  </Box>
                </Grid>
                {/* Labor Wage p/h */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "60px",
                      backgroundColor: (theme) => theme.palette.pure.white,
                      padding: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        marginBottom: 1,
                      }}
                    >
                      Labor Wage p/h
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="0.00"
                      autoComplete="off"
                      InputProps={{
                        disableUnderline: true,
                        inputMode: "numeric",
                        startAdornment: (
                          <InputAdornment position="start">
                            {currencySymbol}
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: "100%" }}
                      value={laborCostDisplay}
                      onChange={(e) => setLaborCostDisplay(e.target.value)}
                      onBlur={handleLaborCostUpdate}
                      onKeyDown={utilFunctions.blurInput}
                      onFocus={utilFunctions.highlightInputOnFocus}
                    />
                  </Box>
                </Grid>
                {/* Labor Duration */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "60px",
                      backgroundColor: (theme) => theme.palette.pure.white,
                      padding: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        marginBottom: 1,
                      }}
                    >
                      Labor Duration
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="00:05"
                      autoComplete="off"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      sx={{ width: "100%" }}
                      value={laborDurationDisplay}
                      onChange={(e) => setLaborDurationDisplay(e.target.value)}
                      onBlur={handleUpdateLaborDuration}
                      error={laborDurationInvalid}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Cost Breakdown */}
          <Grid item xs={12} sm={12} md={6}>
            <Box
              sx={{
                width: "100%",
                borderRadius: 4,
                backgroundColor: (theme) => theme.palette.pure.white,
                border: "2px solid",
                borderColor: (theme) => theme.palette.terrain[300],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: 4,
                  paddingRight: 2,
                }}
              >
                <Typography variant="h3">Costing Breakdown</Typography>
                <IconButton
                  onClick={() => setCostingBreakdownOpen(!costingBreakdownOpen)}
                  size="small"
                >
                  {costingBreakdownOpen ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              </Box>
              <Collapse in={costingBreakdownOpen}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    width: "100%",
                    paddingTop: 4,
                    paddingBottom: 4,
                  }}
                >
                  {getIngredientCostChartData().length > 0 ? (
                    <PieChart
                      series={[
                        {
                          data: getIngredientCostChartData(),
                          highlightScope: {
                            faded: "global",
                            highlighted: "item",
                          },
                          faded: {
                            innerRadius: 30,
                            additionalRadius: -30,
                            color: "gray",
                          },
                          valueFormatter: (item) => {
                            return `${currencySymbol}${utilFunctions.convertCentsToLargerFormatCurrency(
                              item.value * 100,
                              locale,
                              currency,
                            )}`;
                          },
                        },
                      ]}
                      width={400}
                      height={getChartHeight()}
                      margin={{ right: 250, top: 20, bottom: 20 }}
                      slotProps={{
                        legend: {
                          direction: "column",
                          position: {
                            vertical: "middle",
                            horizontal: "right",
                          },
                          labelStyle: {
                            fontSize: 10,
                          },
                          itemMarkWidth: 8,
                          itemMarkHeight: 8,
                          itemGap: 8,
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: (theme) => theme.palette.terrain[500],
                          textAlign: "center",
                        }}
                      >
                        No ingredient cost data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Grid>

          {/* Costing Summary */}
          <Grid item xs={12}>
            <Box
              sx={{
                width: "100%",
                borderRadius: 4,
                backgroundColor: (theme) => theme.palette.pure.white,
                border: "2px solid",
                borderColor: (theme) => theme.palette.terrain[300],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: 4,
                  paddingRight: 2,
                }}
              >
                <Typography variant="h3">Costing Summary</Typography>
                <IconButton
                  onClick={() => setCostingSummaryOpen(!costingSummaryOpen)}
                  size="small"
                >
                  {costingSummaryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={costingSummaryOpen}>
                <Box
                  sx={{
                    padding: 4,
                  }}
                >
                  <Grid container spacing={3}>
                    {/* Menu Price */}
                    <Grid item xs={12} md={2.4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: (theme) => theme.palette.terrain[300],
                          borderRadius: 1,
                          padding: 2,
                          backgroundColor: (theme) => theme.palette.pure.white,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            marginBottom: 2,
                          }}
                        >
                          Menu Price
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="0.00"
                          autoComplete="off"
                          InputProps={{
                            disableUnderline: true,
                            inputMode: "numeric",
                            startAdornment: (
                              <InputAdornment position="start">
                                {currencySymbol}
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: "100%" }}
                          value={menuPriceDisplay}
                          onChange={(e) => setMenuPriceDisplay(e.target.value)}
                          onBlur={handleMenuPriceUpdate}
                          onKeyDown={utilFunctions.blurInput}
                          onFocus={utilFunctions.highlightInputOnFocus}
                        />
                      </Box>
                    </Grid>
                    {/* Total Labor Cost */}
                    <Grid item xs={12} md={2.4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: (theme) => theme.palette.terrain[300],
                          borderRadius: 1,
                          padding: 2,
                          backgroundColor: (theme) => theme.palette.pure.white,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            marginBottom: 2,
                          }}
                        >
                          Total Labor Cost
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minHeight: "34px",
                            backgroundColor: (theme) =>
                              theme.palette.terrain[200],
                            borderRadius: 1,
                            padding: 2,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {currencySymbol}{" "}
                            {utilFunctions.convertCentsToLargerFormatCurrency(
                              recipeCopy?.laborCost?.cost?.amount || 0,
                              locale,
                              currency,
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: (theme) => theme.palette.terrain[600],
                              fontSize: "10px",
                            }}
                          >
                            ({calculatePercentageOfMenuPrice(getRawLaborCost())}
                            %)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {/* Total Ingredient Cost */}
                    <Grid item xs={12} md={2.4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: (theme) => theme.palette.terrain[300],
                          borderRadius: 1,
                          padding: 2,
                          backgroundColor: (theme) => theme.palette.pure.white,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            marginBottom: 2,
                          }}
                        >
                          Total Ingredient Cost
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minHeight: "34px",
                            backgroundColor: (theme) =>
                              theme.palette.terrain[200],
                            borderRadius: 1,
                            padding: 2,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {calculateIngredientCostTotal()}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: (theme) => theme.palette.terrain[600],
                              fontSize: "10px",
                            }}
                          >
                            (
                            {calculatePercentageOfMenuPrice(
                              getRawIngredientCost(),
                            )}
                            %)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {/* Total Cost */}
                    <Grid item xs={12} md={2.4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: (theme) => theme.palette.terrain[300],
                          borderRadius: 1,
                          padding: 2,
                          backgroundColor: (theme) => theme.palette.pure.white,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            marginBottom: 2,
                          }}
                        >
                          Total Cost
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minHeight: "34px",
                            backgroundColor: (theme) =>
                              theme.palette.terrain[200],
                            borderRadius: 1,
                            padding: 2,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {calculateTotalCost(recipeCopy)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: (theme) => theme.palette.terrain[600],
                              fontSize: "10px",
                            }}
                          >
                            ({calculatePercentageOfMenuPrice(getRawTotalCost())}
                            %)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {/* Estimated Profit */}
                    <Grid item xs={12} md={2.4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: (theme) => theme.palette.terrain[300],
                          borderRadius: 1,
                          padding: 2,
                          backgroundColor: (theme) => theme.palette.pure.white,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            marginBottom: 2,
                          }}
                        >
                          Estimated Profit
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minHeight: "34px",
                            backgroundColor: (theme) =>
                              theme.palette.terrain[200],
                            borderRadius: 1,
                            padding: 2,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {currencySymbol}{" "}
                            {utilFunctions.convertCentsToLargerFormatCurrency(
                              getRawEstimatedProfit(),
                              locale,
                              currency,
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: (theme) => theme.palette.terrain[600],
                              fontSize: "10px",
                            }}
                          >
                            (
                            {calculatePercentageOfMenuPrice(
                              getRawEstimatedProfit(),
                            )}
                            %)
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Spec Partner Section */}
      {isSpecEstablishment && (
        <Box sx={{ marginTop: 6 }}>
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="h3">Partner Settings</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 4,
              width: "100%",
              minHeight: "88px",
              borderRadius: 4,
              backgroundColor: (theme) => theme.palette.pure.white,
              border: "2px solid",
              borderColor: (theme) => theme.palette.terrain[300],
            }}
          >
            <Grid
              container
              spacing={3}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isSpecPartnerRecipe}
                      onChange={handleToggleSpecPartner}
                      disabled={toggleSpecPartnerMutation.isLoading}
                      color="primary"
                    />
                  }
                  label="Is Partner Recipe"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl
                  fullWidth
                  disabled={assignSpecPartnerMutation.isLoading}
                >
                  <InputLabel id="spec-partner-label">
                    Select Partner
                  </InputLabel>
                  <Select
                    labelId="spec-partner-label"
                    id="spec-partner-select"
                    value={selectedSpecPartner}
                    label="Select Partner"
                    onChange={handleSpecPartnerChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {specPartnersData?.map((partner) => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}

      {/* Ingredients Section */}

      <Box sx={{ marginTop: 8 }}>
        <Box sx={{ marginBottom: 4 }}>
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Grid item>
              <Typography variant="h3">Ingredients</Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleAddIngredient}
                disabled={addIngredientMutation.isLoading}
              >
                <Typography variant="largeButton">+ Add Ingredient</Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
        <Grid
          item
          sx={{ width: "100%", display: "flex", flexDirection: "row" }}
        >
          <TableContainer
            sx={{
              overflow: "auto",
              borderRadius: 2,
              border: "1px solid",
              borderColor: (theme) => theme.palette.terrain[400],
            }}
          >
            <Table
              sx={{ width: "100%", overflow: "auto" }}
              aria-label="customized table"
            >
              <TableHead sx={{ height: "24px" }}>
                <TableRow sx={{ height: 24 }}>
                  {/* DRAG ICON HEADER */}
                  {/* <TableCell
                    sx={{
                      borderLeft: "none",
                      borderBottom: "1px solid",
                      borderColor: (theme) => theme.palette.terrain[400],
                      background: (theme) => theme.palette.terrain[300],
                      padding: 0,
                      width: "32px !important",
                    }}
                  ></TableCell> */}
                  <TableCell
                    sx={[tableHeadStyle, { width: "80px", borderLeft: "none" }]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Quantity
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Quantity"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                The amount of product or ingredient in the
                                recipe.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[tableHeadStyle, { width: "250px", minWidth: "250px" }]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Ingredient Name
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Ingredient Name"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                The name of product or ingredient in the recipe.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[tableHeadStyle, { width: "160px", minWidth: "160px" }]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Display Name
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Display Name"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                How the ingredient line will display to staff
                                users in the Mobile App.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[tableHeadStyle, { minWidth: "120px", width: "120px" }]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Product Format
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Product Format"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                The name and unit size of the product. This is
                                necessary to accurately calculate the cost of
                                the product or ingredient.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        borderRight: "none",
                        minWidth: "100px",
                        width: "100px",
                      },
                    ]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Purchase Unit
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Purchase Unit"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                Specify which Product Format packaging this
                                product or ingredient comes from. This is an
                                important distinction for costing. For example
                                it is important to distinguish between 1 bottle
                                of something vs. 1 case of something.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        width: "56px !important",
                        padding: 0,
                      },
                    ]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: 1,
                        paddingRight: 1,
                      }}
                    >
                      Training
                      <Box component="div" sx={{ marginRight: 0 }}>
                        <DescriptionToolTip
                          header="Training"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                Toggles whether the ingredient line will be
                                hidden from staff users in the Mobile App.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        width: "56px !important",
                        padding: 0,
                      },
                    ]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: 1,
                        paddingRight: 1,
                      }}
                    >
                      Costing
                      <Box component="div" sx={{ marginRight: 0 }}>
                        <DescriptionToolTip
                          header="Costing"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                Toggles whether the ingredient line will count
                                towards the cost of the entire recipe.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        width: "56px !important",
                        textAlign: "center",
                        padding: 0,
                      },
                    ]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: 1,
                        paddingRight: 1,
                      }}
                    >
                      Garnish
                      <Box component="div" sx={{ marginRight: 0 }}>
                        <DescriptionToolTip
                          header="Garnish"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                Toggles whether the ingredient line is a
                                garnish, thus appearing in the <q>Garnish</q>{" "}
                                section of the Mobile App when viewing as a
                                staff user.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[tableHeadStyle, { width: "84px", minWidth: "84px" }]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Labor Cost
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Labor Cost"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                If the ingredient is another recipe, the
                                associated labor cost (as a portion of the
                                quantity on the ingredeint line) will show here.
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[tableHeadStyle, { width: "95px", minWidth: "95px" }]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Ingredient {currencySymbol}
                      <Box component="div" sx={{ marginRight: -3 }}>
                        <DescriptionToolTip
                          header="Ingredient Cost"
                          description={
                            <Box component="div">
                              <Box sx={{ marginTop: 1 }}>
                                The cost of the ingredient line, either the cost
                                of the product or only the ingredient cost of
                                another recipe (as a portion of the quantity).
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      { width: "57px", borderRight: "none" },
                    ]}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipeCopy.ingredients?.map((x) => (
                  <RecipeIngredientRow
                    key={x.id}
                    openQuantityModal={handleToggleQuantityModal}
                    ingredient={x}
                    productLibrary={productLibrary}
                    productLibraryMap={productLibraryMap}
                    establishmentRecipes={filteredRecipeOptions}
                    updateIngredientCallback={handleIngredientUpdateCallback}
                    deleteIngredientCallback={handleDeleteIngredientCallback}
                    tableCellStyle={tableCellStyle}
                    openCreateModal={handleOpenCreateProductModal}
                  />
                ))}
                <TableRow>
                  {/* COLSPAN NEEDS TO BE CHANGED TO 9 WHEN WE IMPLEMENT THE DRAG
                  ICON */}
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        borderLeft: "none",
                        borderTop: "1px solid",
                        borderColor: (theme) => theme.palette.terrain[400],
                      },
                    ]}
                    colSpan={8}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        borderTop: "1px solid",
                        borderColor: (theme) => theme.palette.terrain[400],
                      },
                    ]}
                  >
                    {calculateLaborCostTotal()}
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        borderTop: "1px solid",
                        borderColor: (theme) => theme.palette.terrain[400],
                      },
                    ]}
                  >
                    {calculateIngredientCostTotal()}
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        borderRight: "none",
                        borderTop: "1px solid",
                        borderColor: (theme) => theme.palette.terrain[400],
                      },
                    ]}
                  ></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Box>

      {/* Training Details */}

      <Box sx={{ marginTop: 8 }}>
        <Box sx={{ marginBottom: 4 }}>
          <Typography variant="h3">Training Details</Typography>
        </Box>
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={[
                tableHeadStyle,
                {
                  width: "33%",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderRight: "none",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTopLeftRadius: 8,
                },
              ]}
            >
              Description
            </Box>
            <Box
              sx={[
                tableHeadStyle,
                {
                  width: "34%",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[400],
                },
              ]}
            >
              Tasting Notes
            </Box>
            <Box
              sx={[
                tableHeadStyle,
                {
                  width: "33%",
                  borderRight: "none",
                  borderTopRightRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderLeft: "none",
                  borderColor: (theme) => theme.palette.terrain[400],
                },
              ]}
            >
              Directions
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box
              sx={[
                tableCellStyle,
                {
                  width: "33%",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTop: "none",
                  borderRight: "none",
                  borderBottomLeftRadius: 8,
                },
              ]}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  height: "100%",
                  backgroundColor: (theme) => theme.palette.pure.white,
                  borderBottomLeftRadius: 8,
                }}
              >
                <TextField
                  variant="filled"
                  multiline
                  fullWidth
                  placeholder="Enter description"
                  autoComplete="off"
                  minRows={5}
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    height: "100%",
                    borderRadius: 0,

                    "& .MuiFilledInput-root": {
                      background: (theme) => theme.palette.pure.white,
                      borderBottomLeftRadius: 8,
                    },
                  }}
                  inputProps={{ style: { marginTop: -20 } }}
                  InputProps={{
                    disableUnderline: true,
                    height: "100%",
                  }}
                  onChange={(e) =>
                    updateLocalRecipeCopy("description", e.target.value)
                  }
                  onBlur={() => handleUpdateRecipe("description")}
                  value={recipeCopy.description}
                />
              </Box>
            </Box>
            <Box
              sx={[
                tableCellStyle,
                {
                  width: "34%",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTop: "none",
                },
              ]}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  height: "100%",
                  backgroundColor: (theme) => theme.palette.pure.white,
                }}
              >
                <TextField
                  variant="filled"
                  multiline
                  fullWidth
                  autoComplete="off"
                  placeholder="Enter tasting notes"
                  minRows={5}
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    height: "100%",
                    borderRadius: 0,
                    "& .MuiFilledInput-root": {
                      background: (theme) => theme.palette.pure.white,
                    },
                  }}
                  InputProps={{ disableUnderline: true, height: "100%" }}
                  inputProps={{ style: { marginTop: -20 } }}
                  onChange={(e) =>
                    updateLocalRecipeCopy("tastingNotes", e.target.value)
                  }
                  onBlur={() => handleUpdateRecipe("tastingNotes")}
                  value={recipeCopy.tastingNotes}
                />
              </Box>
            </Box>
            <Box
              sx={[
                tableCellStyle,
                {
                  width: "33%",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottomRightRadius: 8,
                },
              ]}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  height: "fit-content",
                  backgroundColor: (theme) => theme.palette.pure.white,
                  borderBottomRightRadius: 8,
                }}
              >
                <TextField
                  variant="filled"
                  multiline
                  fullWidth
                  autoComplete="off"
                  placeholder="Enter directions"
                  minRows={5}
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    height: "100%",
                    borderRadius: 0,
                    "& .MuiFilledInput-root": {
                      background: (theme) => theme.palette.pure.white,
                      borderBottomRightRadius: 8,
                    },
                  }}
                  InputProps={{
                    disableUnderline: true,
                    height: "fit-content",
                  }}
                  inputProps={{ style: { marginTop: -20 } }}
                  onChange={(e) =>
                    updateLocalRecipeCopy("directions", e.target.value)
                  }
                  onBlur={() => handleUpdateRecipe("directions")}
                  value={recipeCopy.directions}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <IngredientQuantityModal
        isOpen={quantityModalOpen}
        toggleModalOpen={handleToggleQuantityModal}
        ingredient={ingredientForQuantityModal}
        unitsOfMeasurement={establishmentUoMs}
        updateIngredient={handleUpdateIngredient}
        loading={recipeMutation.isLoading}
      />
      <YieldModal
        isOpen={yieldModalOpen}
        toggleModalOpen={toggleYieldModalOpen}
        recipeYields={recipe?.yield}
        updateRecipe={handleUpdateRecipe}
        unitsOfMeasurement={establishmentUoMs}
        isLoading={recipeMutation.isLoading}
      />
      <EditRecipeModal
        isOpen={editRecipeModalOpen}
        toggleModalOpen={toggleEditRecipeModalOpen}
        headerText="Edit Recipe"
        buttonText="Save Recipe"
        recipe={recipe}
      />
      <CreateProductModal
        isOpen={createProductModalOpen}
        closeModal={handleCloseCreateProductModal}
        createProductRecipeRef={createProductRecipeRef}
        inventorySettings={settings?.inventorySettings}
        vendorData={vendorData}
        expenseGroupData={expenseGroupData}
        familyData={familyData}
        unitsOfMeasurement={establishmentUoMs || []}
        loading={
          createProductMutation.isLoading || ingredientMutation.isLoading
        }
        createProduct={handleCreateProduct}
        createProductPrefill={createProductPrefill}
      />
      <CopyRecipeModal
        copyRecipe={handleCopyRecipe}
        isOpen={copyRecipeModalOpen}
        toggleModalOpen={() => setCopyRecipeModalOpen(!copyRecipeModalOpen)}
        recipeName={recipe?.name}
        recipeId={recipe?.id}
      />
      <RecipePreviewModal
        isOpen={previewModalOpen}
        toggleModalOpen={setPreviewModalOpen}
        recipe={recipeCopy}
        productLibraryMap={productLibraryMap}
        currencySymbol={currencySymbol}
        locale={locale}
        currency={currency}
      />
      <Box>
        <Snackbar
          open={alertSuccess ? true : false}
          onClose={() => setAlertSuccess(undefined)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success" variant="filled">
            {alertSuccess}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default RecipeForm;
